/** @author PVCOMBANK */
import React, { useEffect } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useTheme, Paragraph, Button, HelperText } from "react-native-paper";
import { Block } from "@block";
import HeaderNav from "app/shared/components/header-nav/header-nav";
import { RouteProp, StackActions, useNavigation, useRoute } from "@react-navigation/native";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { TextInput } from "react-native-paper";
import { spacing } from "@styles/spacing";
import { useDesktopMediaQuery } from "@media-query";
import ScreenNameType from "@navigation/nav-screens";
import { useTranslation } from "react-i18next";
import { NavTitleBar, SubtitleNavTitleBar } from "app/shared/components/nav-title-bar/nav-title-bar";
import { StepProcessUniversal } from "app/shared/components/stepper-process/stepper-process";
import { fetchPaymentCardConfirmRequest, setPaymentCardStore } from "../reducer/actions";
import { getPaymentCardStoreSelector } from "../reducer/selectors";
import { useDispatch, useSelector } from "react-redux";
import { ConvertCurrencyToVietnamese, formatNumberThousand } from "app/shared/util/format-currency";
import { Constant } from "app/shared/constant/constant";
import { TextInputMultiline } from "app/shared/components/form/input";
import StackParamsList from "app/navigation/nav-stack-params";

type ProfileScreenRouteProp = RouteProp<StackParamsList, ScreenNameType.PAYMENT_CARD_CONFIRM>;

const PaymentCardConfirm = () => {
  const { colors, fonts } = useTheme();
  const { dispatch, goBack } = useNavigation();
  const dispatchStore = useDispatch();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();
  const routeProps = useRoute<ProfileScreenRouteProp>();

  const convertCurrencyToVietnamese = new ConvertCurrencyToVietnamese();

  const paymentCardStore = useSelector(getPaymentCardStoreSelector);
  const [preScreen, setPreScreen] = React.useState<string>(t("CARD_DICH_VU_THE"));

  const textInputProps: object = {
    theme: { colors: { primary: colors.accent, text: colors.accent } },
    outlineColor: "transparent",
    editable: false,
  };

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textInput: { color: colors.accent, backgroundColor: colors.backgroundForm, fontWeight: "700", textShadowColor: "black" },
        textInputAmount: { fontSize: 18 },
        buttonForm: { width: 128, alignSelf: "center", backgroundColor: colors.backgroundForm },
        desktopDisplay: { display: isDesktop ? "flex" : "none" },
        fieldFormStyles: { color: colors.text, width: 200, fontWeight: "700", fontSize: 16 },
      }),
    [colors.accent, colors.backgroundForm, colors.text, isDesktop],
  );

  const onPressGoBack = () => {
    if (routeProps?.params?.preScreen === ScreenNameType.LIST_PAYMENTS) dispatch(StackActions.replace(ScreenNameType.LIST_PAYMENTS));
    else dispatch(StackActions.replace(ScreenNameType.LIST_CARDS));
  };

  const subtitlesArrays: Array<SubtitleNavTitleBar> = [
    {
      name: t("FUNC_TRANG_CHU"),
      onPressSubtitle: () => {
        dispatch(StackActions.replace(ScreenNameType.HOME));
      },
    },
    {
      name: preScreen,
      onPressSubtitle: () => onPressGoBack(),
    },
    {
      name: t("CARD_THANH_TOAN_THE_TIN_DUNG"),
      onPressSubtitle: () => {},
    },
  ];

  const onPressCancel = () => {
    dispatchStore(setPaymentCardStore({ ...paymentCardStore, isCreated: false, responseCreate: undefined }));
    goBack();
  };

  const onPressAccept = () => {
    dispatchStore(fetchPaymentCardConfirmRequest());
  };

  const FieldForm = ({ name = "" }) => (
    <View style={styles.desktopDisplay}>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFormStyles}>{name}</Paragraph>
      <StackSpacing size="medium" />
    </View>
  );

  const FormAccountHolder = (
    <Block ph={isDesktop ? spacing.galactic : spacing.large} row={isDesktop ? true : false} pt={spacing.medium}>
      <FieldForm name={t("TRANFER_TAI_KHOAN_NGUON")} />
      <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
        <TextInput
          {...textInputProps}
          style={styles.textInput}
          label={t("TRANFER_TAI_KHOAN_NGUON")}
          value={paymentCardStore.dataCreate?.ACCOUNT_DEBIT?.ACCOUNTNO}
        />
        <TextInput
          {...textInputProps}
          style={styles.textInput}
          label={t("ACC_SO_DU_KHA_DUNG")}
          value={formatNumberThousand(paymentCardStore.dataCreate?.ACCOUNT_DEBIT?.AVAILABLEBALANCE)}
          right={<TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />}
        />
      </Block>
    </Block>
  );

  const FormTransfer = (
    <Block ph={isDesktop ? spacing.galactic : spacing.large} row={isDesktop ? true : false} pt={spacing.large}>
      <FieldForm name={t("CARD_THANH_TOAN_THE_TIN_DUNG_THONG_TIN_THANH_TOAN")} />
      <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
        <TextInputMultiline
          name="CARD_DICH_VU"
          {...textInputProps}
          style={styles.textInput}
          label={t("CARD_DICH_VU")}
          value={
            paymentCardStore.dataCreate?.TYPE === Constant.CARD.CARD_PAYMENT_TYPE.CRD
              ? t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_CA_NHAN")
              : t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_HO")
          }
        />
        <Block spaced row={isDesktop ? true : false}>
          <Block flex={1}>
            <TextInput
              {...textInputProps}
              style={styles.textInput}
              label={t("CARD_THANH_TOAN_THE_TIN_DUNG_SO_THE")}
              value={paymentCardStore.dataCreate?.CARD?.cardNum}
            />
          </Block>
          <QueueSpacing size="huge" />
          <Block flex={1}>
            <TextInputMultiline
              name="CARD_THANH_TOAN_THE_TIN_DUNG_CHU_THE"
              {...textInputProps}
              style={styles.textInput}
              label={t("CARD_THANH_TOAN_THE_TIN_DUNG_CHU_THE")}
              value={paymentCardStore.responseCreate?.destinationCardName}
            />
          </Block>
        </Block>
        {paymentCardStore.dataCreate?.TYPE === Constant.CARD.CARD_PAYMENT_TYPE.CRD ? (
          <Block spaced row={isDesktop ? true : false}>
            <Block flex={1}>
              <TextInput
                {...textInputProps}
                style={styles.textInput}
                label={t("CARD_THANH_TOAN_THE_TIN_DUNG_TONG_DU_NO")}
                value={formatNumberThousand(paymentCardStore.dataCreate?.CARD?.totalAmount)}
                right={<TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />}
              />
            </Block>
            <QueueSpacing size="huge" />
            <Block flex={1}>
              <TextInput
                {...textInputProps}
                style={styles.textInput}
                label={t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TOI_THIEU")}
                value={formatNumberThousand(paymentCardStore.dataCreate?.CARD?.mandatoryAmount)}
                right={<TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />}
              />
            </Block>
          </Block>
        ) : (
          <Block />
        )}
        <TextInput
          {...textInputProps}
          style={[styles.textInput, styles.textInputAmount]}
          label={t("CARD_THANH_TOAN_THE_TIN_DUNG_SO_TIEN_THANH_TOAN")}
          value={formatNumberThousand(paymentCardStore.dataCreate?.AMOUNT)}
          right={<TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />}
        />
        <HelperText type="info" visible={true} style={{ color: colors.accent }}>
          {convertCurrencyToVietnamese.convert(Number(paymentCardStore.dataCreate?.AMOUNT || 0))}
        </HelperText>
        <TextInputMultiline
          name="CARD_THANH_TOAN_THE_TIN_DUNG_NOI_DUNG"
          {...textInputProps}
          style={styles.textInput}
          label={t("CARD_THANH_TOAN_THE_TIN_DUNG_NOI_DUNG")}
          value={paymentCardStore.dataCreate?.DESCRIPTION}
        />
      </Block>
    </Block>
  );

  const ButtonRow = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button mode="contained" style={styles.buttonForm} onPress={onPressCancel}>
        {t("COMMON_QUAY_LAI")}
      </Button>
      <QueueSpacing size="large" />
      <Button onPress={onPressAccept} mode="contained">
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    if (routeProps?.params?.preScreen) {
      const value = routeProps?.params?.preScreen === ScreenNameType.LIST_PAYMENTS ? t("FUNC_DV_THANH_TOAN") : t("CARD_DICH_VU_THE");
      setPreScreen(value);
    }
  }, [routeProps?.params?.preScreen, t]);

  useEffect(() => {
    if (paymentCardStore.isConfirm) {
      dispatch(StackActions.replace(ScreenNameType.PAYMENT_CARD_AUTH, { preScreen: routeProps?.params?.preScreen }));
    }
  }, [paymentCardStore.isConfirm, dispatch, routeProps?.params?.preScreen]);

  return (
    <Block flex={1} bg={colors.surface}>
      <ScrollView>
        <HeaderNav />
        <NavTitleBar title={t("CARD_THANH_TOAN_THE_TIN_DUNG")} subtitles={subtitlesArrays} />
        <StepProcessUniversal currentSteps={1} />
        {FormAccountHolder}
        {FormTransfer}
        {ButtonRow}
      </ScrollView>
    </Block>
  );
};

export default PaymentCardConfirm;
